import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useUserRoles } from '../hooks/useUserRoles';
import { JourneyTracker } from '../utils/journeyTracking';
import { ConversionTracker } from '../utils/conversionTracking';
import { EmailTriggers } from '../utils/emailTriggers';
import { ABTestTracker } from '../utils/abTestTracker';
import { consentManager } from '../services/consentManager';

// Enhanced Omnisend integration component with advanced tracking
export const OmnisendIntegration = () => {
  const { isApiAuthReady, userBillingName } = useAuthStore();
  const { roles: userRoles } = useUserRoles();

  // Initialize journey tracking on component mount
  useEffect(() => {
    // Only initialize if user has marketing consent
    const hasMarketingConsent = consentManager.hasConsentFor('marketing');
    
    if (isApiAuthReady && hasMarketingConsent) {
      // Start journey tracking if not already started
      const currentJourney = JourneyTracker.getCurrentJourney();
      if (!currentJourney) {
        const source = sessionStorage.getItem('original_source') || 'direct';
        JourneyTracker.startJourney(source);
      }

      // Track page view
      JourneyTracker.trackPageSequence(window.location.pathname, {
        authenticated: true,
        userRoles: userRoles
      });
    } else if (isApiAuthReady && !hasMarketingConsent) {
      console.log('[OmnisendIntegration] Marketing consent not granted, skipping journey tracking');
    }
  }, [isApiAuthReady, userRoles]);

  // Enhanced user identification with role-based segmentation
  useEffect(() => {
    // Only initialize Omnisend if user has marketing consent
    const hasMarketingConsent = consentManager.hasConsentFor('marketing');
    
    if (isApiAuthReady && userBillingName && hasMarketingConsent && window.omnisend) {
      // Parse billing name if it exists
      const nameParts = userBillingName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Determine user segment
      const userSegment = userRoles.length > 0 ? userRoles[0] : 'unknown';
      const isTrialUser = userRoles.includes('trial-subscriber');
      const isPaidUser = userRoles.some(role => 
        ['basic-subscriber', 'standard-subscriber', 'elite-subscriber'].includes(role)
      );

      // Enhanced contact identification
      window.omnisend.push([
        'identifyContact',
        {
          email: '', // Will be set by your backend API
          firstName: firstName,
          lastName: lastName,
          customProperties: {
            last_login: new Date().toISOString(),
            page_tracking: 'enabled',
            user_segment: userSegment,
            subscription_type: isTrialUser ? 'trial' : isPaidUser ? 'paid' : 'none',
            ab_test_variants: JSON.stringify(ABTestTracker.getAllActiveVariants()),
            journey_insights: JSON.stringify(JourneyTracker.getJourneyInsights()),
            registration_date: localStorage.getItem('registration_date') || new Date().toISOString()
          }
        }
      ]);

      // Track enhanced login event
      ConversionTracker.trackFeatureInteraction('dashboard_login', 'authenticate');

      // Setup email triggers based on user type
      if (isTrialUser) {
        EmailTriggers.setupTrialNurturingSequence('user_id', 'user_email', 'trial');
        
        // Check for trial abandonment recovery
        const lastLogin = localStorage.getItem('last_login_date');
        if (lastLogin) {
          const daysSinceLogin = Math.floor((Date.now() - parseInt(lastLogin)) / (1000 * 60 * 60 * 24));
          if (daysSinceLogin >= 3) {
            EmailTriggers.setupReengagementSequence('user_id', 'user_email', daysSinceLogin);
          }
        }
      }

      // Update last login
      localStorage.setItem('last_login_date', Date.now().toString());
    }
  }, [isApiAuthReady, userBillingName, userRoles]);

  // Track page navigation with enhanced context
  useEffect(() => {
    const hasMarketingConsent = consentManager.hasConsentFor('marketing');
    
    if (isApiAuthReady && hasMarketingConsent && window.omnisend) {
      // Track comprehensive page view
      window.omnisend.push([
        'track',
        'enhanced_page_view',
        {
          page: window.location.pathname,
          timestamp: new Date().toISOString(),
          user_segment: userRoles.length > 0 ? userRoles[0] : 'unknown',
          device_type: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
          session_duration: JourneyTracker.getSessionDuration(),
          ab_tests: ABTestTracker.getAllActiveVariants()
        }
      ]);

      // Track specific dashboard features viewed
      if (window.location.pathname === '/dashboard') {
        ConversionTracker.trackFeatureInteraction('dashboard', 'view');
      }
    }
  }, [isApiAuthReady, window.location.pathname, userRoles]);

  // Listen for consent changes
  useEffect(() => {
    const unsubscribe = consentManager.onConsentChange((preferences) => {
      if (preferences.marketing && window.omnisend) {
        // User just granted marketing consent, initialize tracking
        console.log('[OmnisendIntegration] Marketing consent granted, enabling tracking');
        
        // Track consent given event
        window.omnisend.push(['track', 'marketing_consent_granted', {
          timestamp: new Date().toISOString(),
          user_id: userBillingName || 'anonymous'
        }]);
      } else if (!preferences.marketing) {
        console.log('[OmnisendIntegration] Marketing consent withdrawn, disabling enhanced tracking');
      }
    });
    
    return unsubscribe;
  }, [userBillingName]);

  return null; // This component doesn't render anything
};

// Enhanced helper function to track custom events with conversion context
export const trackOmnisendEvent = (eventName: string, properties: any = {}) => {
  if (window.omnisend) {
    const enhancedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      session_id: sessionStorage.getItem('ab_session_id'),
      user_journey_step: JourneyTracker.getCurrentJourney()?.steps.length || 0,
      ab_test_context: ABTestTracker.getAllActiveVariants()
    };
    
    window.omnisend.push(['track', eventName, enhancedProperties]);
    console.log(`[EnhancedOmnisend] Tracked: ${eventName}`, enhancedProperties);
  }
};

// Enhanced helper function to identify contact with conversion context
export const identifyOmnisendContact = (contactData: any) => {
  if (window.omnisend) {
    const enhancedContactData = {
      ...contactData,
      customProperties: {
        ...contactData.customProperties,
        journey_stage: JourneyTracker.getCurrentJourney()?.userType || 'unknown',
        conversion_funnel_position: ConversionTracker ? 'identified' : 'unknown',
        ab_test_participation: Object.keys(ABTestTracker.getAllActiveVariants()).length > 0,
        last_tracking_update: new Date().toISOString()
      }
    };
    
    window.omnisend.push(['identifyContact', enhancedContactData]);
    console.log('[EnhancedOmnisend] Contact identified with enhanced data');
  }
};

// New helper function to track conversion funnel progression
export const trackConversionFunnelStep = (step: string, context: any = {}) => {
  ConversionTracker.trackFeatureInteraction('conversion_funnel', step);
  trackOmnisendEvent('conversion_funnel_progression', {
    funnel_step: step,
    ...context
  });
};

// New helper function to track trial-specific events
export const trackTrialEvent = (eventType: string, trialData: any = {}) => {
  const trialDay = Math.floor((Date.now() - parseInt(localStorage.getItem('trial_start_time') || '0')) / (1000 * 60 * 60 * 24));
  
  trackOmnisendEvent(`trial_${eventType}`, {
    trial_day: trialDay,
    trial_progress: trialDay / 14 * 100, // Assuming 14-day trial
    ...trialData
  });
};

// New helper function to track feature adoption
export const trackFeatureAdoption = (feature: string, adoptionType: 'first_use' | 'regular_use' | 'power_use') => {
  ConversionTracker.trackFeatureInteraction(feature, adoptionType);
  trackOmnisendEvent('feature_adoption', {
    feature_name: feature,
    adoption_type: adoptionType,
    feature_category: feature.split('_')[0] // e.g., 'dashboard' from 'dashboard_analytics'
  });
};

// New helper function to setup comprehensive tracking for new users
export const initializeUserTracking = (userId: string, userEmail: string, userType: 'trial' | 'paid' | 'free') => {
  // Initialize journey
  JourneyTracker.startJourney('registration', userId);
  
  // Track user registration
  ConversionTracker.trackTrialStarted(userType, 'registration');
  
  // Setup appropriate email sequences
  switch (userType) {
    case 'trial':
      EmailTriggers.setupTrialNurturingSequence(userId, userEmail, 'trial');
      break;
    case 'paid':
      EmailTriggers.setupBehavioralTriggers(userId, userEmail);
      break;
  }
  
  // Track A/B test assignments
  ABTestTracker.getAllActiveVariants(userId);
  
  // Store registration metadata
  localStorage.setItem('registration_date', new Date().toISOString());
  localStorage.setItem('user_type', userType);
};

// Declare global omnisend type
declare global {
  interface Window {
    omnisend: any[];
  }
}